import type { paperStrategy } from '@whaletail/signal-engine';
import { formatPercent, formatUsd } from '../../lib/format';
import { SHADOW_BUST_NOTE, shadowRunsLine } from './shadowRuns';
import styles from './Btc15MartingaleComparison.module.css';

function teaser(eligibleRounds: number): string {
  return eligibleRounds > 0 ? `${eligibleRounds} rounds replayed` : '—';
}

/**
 * Retrospective-only: replays martingale sizing (`v2/sizingPolicies.ts`'s
 * `martingaleSizingPolicy()` — double the cap after every loss, reset on a
 * win) against the same real settled "V1 + take-profit" rounds, exit
 * policy held fixed at take-profit-default. Deliberately its own separate
 * card rather than a row in Btc15ShadowSizingPolicyComparison: martingale
 * is a materially different (and more dangerous) kind of strategy than
 * quarter-Kelly/confidence-scaled/profit-scaled — see "On martingale
 * sizing" in packages/signal-engine/src/v2/README.md — so it gets its own
 * visibly-flagged, separately-tracked comparison instead of blending into
 * the "currently tested" set. This never changes which sizing rule is
 * actually live — calculateAllocation and every live P&L replay are
 * completely unaffected by anything shown here. Collapsed by default, same
 * pattern as every other shadow-comparison card.
 */
export function Btc15MartingaleComparison({
  eligibleRounds,
  martingaleDollarPnl,
}: {
  eligibleRounds: number;
  martingaleDollarPnl: paperStrategy.ShadowPolicyDollarPnlSummary[];
}) {
  const summary = martingaleDollarPnl[0];

  return (
    <details className={styles.details}>
      <summary className={styles.summary}>
        <span className={styles.summaryLabel}>
          <span>Martingale sizing (retrospective)</span>
          <span className={styles.riskBadge}>HIGH RISK</span>
        </span>
        <span className={styles.teaser}>{teaser(eligibleRounds)}</span>
      </summary>
      <div className={styles.body}>
        <p className={styles.note}>
          Doubles the max-buy cap after every consecutive loss and resets on a win — the
          classic (and classically dangerous) martingale progression, kept entirely
          separate from the other shadowed sizing policies. Purely retrospective and never
          changes which sizing rule is actually live. See "On martingale sizing" in
          packages/signal-engine/src/v2/README.md before reading too much into a short
          backtest — this strategy's whole risk is a losing streak longer than any seen so
          far. Its loss streak starts over with each new run. {SHADOW_BUST_NOTE}
        </p>
        {!summary || eligibleRounds === 0 ? (
          <p className={styles.placeholder}>No settled, funded rounds to replay yet.</p>
        ) : (
          <div className={styles.row}>
            <span className={styles.stat}>{summary.trades} trades</span>
            <span className={styles.stat}>{formatUsd(summary.stake)} staked</span>
            <span
              className={`${styles.stat} ${summary.pnl >= 0 ? styles.pnlPositive : styles.pnlNegative}`}
            >
              {formatUsd(summary.pnl)} P&amp;L after fees ({formatPercent(summary.roi, 0)}{' '}
              ROI)
            </span>
            <span className={styles.stat}>{shadowRunsLine(summary)}</span>
          </div>
        )}
      </div>
    </details>
  );
}
